/**
 * resumeProbes — os canais que `verify-ok` NÃO prova (BUG-197, 4ª fatia).
 *
 * O build 87 fechou o quarto ramo do discriminador: `wake → paint-ok →
 * verify-ok`, zero listener morto, sentinela nativa vendo CONTEÚDO na tela —
 * e o fundador ainda com "o bug". Ou seja: o stream de LEITURA do Firestore
 * está vivo, o JS produz frames, o compositor apresenta. O que nunca foi
 * medido são os OUTROS canais que o app usa depois de acordar:
 *
 *  · o stream de ESCRITA do Firestore — é por ele que "marcar página",
 *    "adicionar à estante" e todo `setDoc` passam. "Para de ter ações" é
 *    literalmente o sintoma de um write stream morto com o watch vivo.
 *  · a rede das CAPAS (`wsrv.nl`) — pool HTTP/2 por host no NetworkProcess
 *    do WebKit; depois do sono, uma conexão morta que o SO ainda não fechou
 *    faz TODO `<img>` daquele host pendurar. É o print do BUG-196 (capas em
 *    caixa vazia, "nem <img> carrega").
 *  · as CALLABLES (`cloudfunctions.net`) — outro host, outro pool. ⚠️ A sonda
 *    mira o HOST, nunca uma callable (BUG-242, ver `FUNCTIONS_HOST_PROBE_URL`).
 *
 * Cada sonda é injetável (a operação vem de fora) para o teste não precisar
 * de rede nem de Firestore. Nenhuma custa leitura ou escrita cobrada: a de
 * escrita mira um doc que as rules NEGAM (escrita negada não é faturada e
 * `permission-denied` prova a ida e volta — o mesmo truque do verify de
 * leitura); as de host são `fetch` em `no-cors` cujo desfecho é uma resposta
 * opaca, e resposta opaca ainda é resposta.
 */

export type HostProbeVerdict = "ok" | "hung" | "failed";

/** Teto por host. Rede de celular em recuperação pode levar segundos; acima
 * disto, um `<img>` do app já estaria em caixa vazia há um bom tempo. */
export const HOST_PROBE_TIMEOUT_MS = 8_000;

/** Origem das capas proxificadas (BUG-13/BUG-99) e a das callables. */
export const IMAGE_HOST_PROBE_URL = "https://wsrv.nl/";
/**
 * ⚠️ BUG-242: a sonda NÃO pode mirar uma callable. Até 14/09 o alvo era
 * `/checkBetaAccess`, e um `GET` numa callable faz o `firebase-functions`
 * responder 400 E gravar `logger.error("Invalid request")` ANTES de o nosso
 * código rodar — cada retomada de cada app nativo virava 1 erro na métrica
 * `function_errors_total` (71% de tudo que o alarme contou em 7 dias; 1.539
 * entradas). Medido com `curl` + Cloud Logging em 14/09: GET e HEAD numa
 * callable logam; um caminho INEXISTENTE do mesmo host devolve 404 no
 * frontend do Google e nem chega a uma revisão do Cloud Run — zero log, zero
 * invocação, e prova exatamente o que a sonda quer (DNS + TLS + pool HTTP/2
 * do host). O prefixo `__` garante que nunca colide com uma function: nome
 * de function começa obrigatoriamente com letra.
 */
export const FUNCTIONS_HOST_PROBE_URL =
  "https://us-central1-lura-e2b70.cloudfunctions.net/__livra_probe";

/**
 * "O host responde?" — resolve `ok` quando a operação assenta (mesmo com
 * resposta opaca), `hung` quando estoura o teto, `failed` quando rejeita
 * antes do teto (DNS, conexão recusada). `hung` × `failed` importa: pool
 * HTTP/2 morto PENDURA, host fora do ar FALHA rápido.
 */
export async function probeHost(
  request: (signal: AbortSignal) => Promise<unknown>,
  timeoutMs: number = HOST_PROBE_TIMEOUT_MS,
): Promise<HostProbeVerdict> {
  const ctrl = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;

  // O veredito NÃO depende de o request honrar o abort: a corrida contra o
  // teto decide sozinha, e o abort é cortesia (solta a conexão pendurada).
  // Um `fetch` que ignorasse o signal deixaria a sonda presa junto — e a
  // sonda existe justamente para medir o que fica preso.
  const settled: Promise<HostProbeVerdict> = request(ctrl.signal).then(
    () => "ok" as const,
    () => (ctrl.signal.aborted ? ("hung" as const) : ("failed" as const)),
  );
  const expired = new Promise<HostProbeVerdict>((resolve) => {
    timer = setTimeout(() => {
      ctrl.abort();
      resolve("hung");
    }, timeoutMs);
  });

  try {
    return await Promise.race([settled, expired]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export const defaultHostRequest = (url: string) => (signal: AbortSignal) =>
  fetch(url, { mode: "no-cors", cache: "no-store", signal });

export interface DomImageHealth {
  total: number;
  loaded: number;
  broken: number;
  pending: number;
}

/**
 * Foto do estado das `<img>` visíveis no documento — é o que separa "as capas
 * não CHEGAM" (pending alto) de "as capas FALHARAM" (broken alto) de "está
 * tudo carregado e o sintoma é outro" (loaded ≈ total).
 */
export function summarizeDomImages(root: ParentNode): DomImageHealth {
  const imgs = Array.from(root.querySelectorAll("img"));
  let loaded = 0;
  let broken = 0;
  let pending = 0;
  for (const img of imgs) {
    if (!img.complete) pending += 1;
    else if (img.naturalWidth > 0) loaded += 1;
    else broken += 1;
  }
  return { total: imgs.length, loaded, broken, pending };
}

export function formatDomImages(h: DomImageHealth): string {
  return `ok=${h.loaded} broken=${h.broken} pending=${h.pending} total=${h.total}`;
}

/**
 * Foto do estado do DOM na retomada (BUG-197, 5ª fatia).
 *
 * Todas as sondas de JS até aqui mediam TRANSPORTE ou FRAMES; nenhuma dizia
 * o que o documento ESTÁ mostrando. "Tela creme" tem duas leituras possíveis
 * e elas se separam aqui: se o `<html>` perdeu o `data-theme` (Linho =
 * `#f4f1ec`, o MESMO creme do fundo nativo) ou o `#root` ficou vazio ou a
 * nav sumiu, o creme é do DOM — bug NOSSO, curável no React. Se o DOM está
 * inteiro, temático, com nav e conteúdo, o creme vem de fora do documento.
 *
 * O `dom-imgs` da 4ª fatia já provou o DOM com 38 `<img>` no episódio — esta
 * linha fecha o resto: tema, papel, casca, nav, viewport e scroll.
 */
export function summarizeDomState(doc: Document, win: Window): string {
  const html = doc.documentElement;
  const theme = html.getAttribute("data-theme") ?? "none";
  let paper = "?";
  try {
    paper = win.getComputedStyle(doc.body).backgroundColor.replace(/\s+/g, "");
  } catch {
    // jsdom/ambiente sem estilo computado: fica "?"
  }
  const root = doc.getElementById("root");
  const rootChildren = root ? root.childElementCount : -1;
  const nav = doc.querySelector("nav") ? 1 : 0;
  const vv = win.visualViewport;
  const viewport = vv
    ? `${Math.round(vv.width)}x${Math.round(vv.height)}@${vv.scale.toFixed(2)}`
    : `${win.innerWidth}x${win.innerHeight}`;
  const scroll = Math.round(win.scrollY || html.scrollTop || 0);
  const hidden = html.style.visibility === "hidden" || html.style.opacity !== "" ? 1 : 0;
  const base = `theme=${theme} paper=${paper} root=${rootChildren} nav=${nav} vv=${viewport} scroll=${scroll} htmlStyled=${hidden}`;
  return `${base} ${summarizeDomCensus(doc)}`;
}

/**
 * Censo do DOCUMENTO na retomada (BUG-197, 6ª fatia).
 *
 * O relato de 01/09 mudou o eixo do diagnóstico: o creme só acontece na HOME
 * e no PERFIL — as outras três abas voltam sãs. Cinco rodadas de diário e
 * NENHUMA linha dizia em que página o episódio aconteceu; esta é a primeira.
 *
 * Junto vai o que as duas páginas têm e as outras não, contado no DOM real
 * (não presumido do código): as camadas que o WebKit compõe em separado e
 * que dependem do processo de GPU do iOS 18 — `rings` (moldura de avatar
 * com animação infinita de `transform`/`filter` — Home header e hero do
 * Perfil), `blur` (`backdrop-filter`), `snap` (rails com scroll-snap, que
 * viram layers de scroll assíncrono) e `anim` (animações vivas: se depois do
 * background elas voltarem PAUSADAS, é o compositor que não retomou). Tudo
 * `querySelectorAll` por classe — custo de microssegundos, sem estilo
 * computado, sem layout.
 */
export function summarizeDomCensus(doc: Document): string {
  const path = doc.location?.pathname ?? "?";
  let running = 0;
  let paused = 0;
  let total = 0;
  try {
    const anims = typeof doc.getAnimations === "function" ? doc.getAnimations() : [];
    total = anims.length;
    for (const a of anims) {
      if (a.playState === "running") running += 1;
      else if (a.playState === "paused") paused += 1;
    }
  } catch {
    // motor sem Web Animations: fica 0/0/0
  }
  const count = (selector: string) => {
    try {
      return doc.querySelectorAll(selector).length;
    } catch {
      return -1;
    }
  };
  const rings = count('[class*="lb-border-"]');
  const blur = count('[class*="backdrop-blur"]');
  const snap = count('[class*="snap-x"]');
  // Bissecção de 01/09 (noite): sete telas passaram e o fecho de imports
  // exclusivo de Home + Perfil é VAZIO — não é componente, é estado. As duas
  // formas de estado que congelam toque e pintura ao mesmo tempo são um
  // overlay aberto (host de overlay com filhos, `role=dialog`) e um elemento
  // fixo invisível engolindo o toque: `hit=` é quem responderia a um toque no
  // CENTRO da tela agora — `main`/`section` são saúde; `div.fixed`, um
  // scrim ou o próprio `#app-overlay-host` são o defeito com nome.
  const overlayHost = doc.getElementById("app-overlay-host");
  const overlay = overlayHost ? overlayHost.childElementCount : -1;
  const dialogs = count('[role="dialog"]');
  let hit = "?";
  try {
    const w = doc.defaultView;
    const el = w ? doc.elementFromPoint(w.innerWidth / 2, w.innerHeight / 2) : null;
    if (el) {
      const cls = typeof el.className === "string" ? el.className.trim().split(/\s+/)[0] ?? "" : "";
      hit = el.tagName.toLowerCase() + (el.id ? `#${el.id}` : cls ? `.${cls}` : "");
    } else {
      hit = "none";
    }
  } catch {
    // motor sem elementFromPoint: fica "?"
  }
  return `path=${path} anim=${running}/${paused}/${total} rings=${rings} blur=${blur} snap=${snap} overlay=${overlay} dialogs=${dialogs} hit=${hit}`;
}
